import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { richHtml } from "../../lib/sanitize";
import { Modal } from "../../components/ui-modal";
import { fmtDateTime } from "../../lib/helpers";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import Switch from "../../components/Switch";
import { useConfirm } from "../../components/ConfirmDialog";

const SAMPLE = { prenom: "Sarah", client: "Clinique Dental Care", nature: "Cabinet dentaire", projet: "Refonte du site", mission: "Montage vidéo — Reel", collaborateur: "Sofiane", montant: "1 500 €", date_echeance: "15/08/2026", phase: "Production", statut_phase: "Validée", numero_facture: "FAC-2026-014", email: "sarah@exemple.com" };
const render = (t, s) => Object.entries(s).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, v), t || "");

const STATUS_STYLE = { "envoyé": "bg-bleu-50 text-bleu", "délivré": "bg-bleu-50 text-bleu", "simulé": "bg-ambre-50 text-ambre-texte", "échoué": "bg-rouge-50 text-rouge", "rejeté": "bg-rouge-50 text-rouge" };

const emptyForm = { name: "", event: "", recipients: [], subject: "", body: "", signature: "", delay_value: 0, delay_unit: "hours" };

export default function AdminEmailAutomations() {
  const { confirm } = useConfirm();
  const [tab, setTab] = useState("scenarios");
  const [items, setItems] = useState([]);
  const [logs, setLogs] = useState([]);
  const [catalog, setCatalog] = useState({ events: [], recipient_roles: [], delay_units: [] });
  const [editor, setEditor] = useState(null); // { mode, id }
  const [form, setForm] = useState(emptyForm);
  const [testEmail, setTestEmail] = useState("");
  const [catFilter, setCatFilter] = useState("all");

  const load = () => api.get("/admin/email-automations").then((r) => setItems(r.data));
  const loadLogs = () => api.get("/admin/email-logs").then((r) => setLogs(r.data));
  useEffect(() => {
    api.get("/admin/automation-events").then((r) => setCatalog(r.data));
    load(); loadLogs();
  }, []);

  const eventByKey = useMemo(() => Object.fromEntries(catalog.events.map((e) => [e.key, e])), [catalog]);
  const roleLabel = useMemo(() => Object.fromEntries(catalog.recipient_roles.map((r) => [r.key, r.label])), [catalog]);
  const unitLabel = useMemo(() => Object.fromEntries((catalog.delay_units || []).map((u) => [u.key, u.label])), [catalog]);

  const currentEvent = eventByKey[form.event] || null;

  const categories = useMemo(() => {
    const counts = {};
    items.forEach((a) => { const c = a.event_category || "Autres"; counts[c] = (counts[c] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0], "fr"));
  }, [items]);
  const filteredItems = useMemo(
    () => (catFilter === "all" ? items : items.filter((a) => (a.event_category || "Autres") === catFilter)),
    [items, catFilter]
  );

  const delayText = (a) => {
    const ev = eventByKey[a.event];
    if (!ev) return "—";
    if (ev.mode === "scheduled") return `${a.delay_value || 0} ${unitLabel[a.delay_unit] || a.delay_unit || "j"} ${ev.schedule_direction === "before" ? "avant échéance" : "après échéance"}`;
    if (!a.delay_value) return "Immédiat";
    return `+${a.delay_value} ${unitLabel[a.delay_unit] || a.delay_unit}`;
  };

  const toggleActive = async (a) => { await api.put(`/admin/email-automations/${a.id}`, { active: !a.active }); toast.success(!a.active ? "Automatisation activée" : "Automatisation désactivée"); load(); };

  const openCreate = () => {
    const first = catalog.events[0];
    setEditor({ mode: "create" });
    setForm({ ...emptyForm, event: first?.key || "", recipients: first?.recipients || [] });
    setTestEmail("");
  };
  const openEdit = (a) => {
    setEditor({ mode: "edit", id: a.id });
    setForm({ name: a.name, event: a.event, recipients: a.recipients || [], subject: a.subject, body: a.body, signature: a.signature || "", delay_value: a.delay_value || 0, delay_unit: a.delay_unit || "hours" });
    setTestEmail("");
  };

  const onEventChange = (key) => {
    const ev = eventByKey[key];
    const allowed = ev?.recipients || [];
    setForm((f) => ({ ...f, event: key, recipients: (f.recipients || []).filter((r) => allowed.includes(r)) }));
  };
  const toggleRecipient = (r) => setForm((f) => ({ ...f, recipients: f.recipients.includes(r) ? f.recipients.filter((x) => x !== r) : [...f.recipients, r] }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Donnez un nom à l'automatisation");
    if (!form.event) return toast.error("Choisissez un déclencheur");
    if (!form.recipients.length) return toast.error("Choisissez au moins un destinataire");
    try {
      if (editor.mode === "create") { await api.post("/admin/email-automations", form); toast.success("Automatisation créée"); }
      else { await api.put(`/admin/email-automations/${editor.id}`, form); toast.success("Automatisation enregistrée"); }
      setEditor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'enregistrement"); }
  };

  const del = async (a) => {
    if (!(await confirm({ title: `Supprimer l'automatisation « ${a.name} » ?`, confirmLabel: "Supprimer", tone: "danger" }))) return;
    await api.delete(`/admin/email-automations/${a.id}`); toast.success("Automatisation supprimée"); load();
  };

  const sendTest = async () => {
    if (!testEmail.trim()) return toast.error("Saisissez une adresse e-mail");
    try {
      if (editor.mode === "edit") await api.put(`/admin/email-automations/${editor.id}`, form);
      const targetId = editor.mode === "edit" ? editor.id : null;
      if (!targetId) return toast.error("Enregistrez d'abord l'automatisation pour envoyer un test");
      await api.post(`/admin/email-automations/${targetId}/test`, { to_email: testEmail.trim() });
      toast.success("E-mail de test envoyé"); loadLogs();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'envoi"); }
  };

  const previewSubject = render(form.subject, SAMPLE);
  const previewBody = render(form.body, SAMPLE) + (form.signature ? `<br/><br/><em>${render(form.signature, SAMPLE)}</em>` : "");

  return (
    <div>
      <PageTitle overline="Automatisations" title="E-mails automatiques (Resend)"
        action={<button onClick={openCreate} data-testid="create-automation" className="bg-marine text-white rounded-sm px-5 py-2.5 text-sm hover:bg-marine-hover transition-colors inline-flex items-center gap-2"><Icons.Plus size={16} /> Créer une automatisation</button>} />

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("scenarios")} data-testid="tab-scenarios" className={`px-4 py-2 text-sm rounded-sm transition-colors ${tab === "scenarios" ? "bg-marine text-white" : "bg-canvas text-marine border border-marine/15"}`}>Scénarios ({items.length})</button>
        <button onClick={() => { setTab("history"); loadLogs(); }} data-testid="tab-history" className={`px-4 py-2 text-sm rounded-sm transition-colors ${tab === "history" ? "bg-marine text-white" : "bg-canvas text-marine border border-marine/15"}`}>Historique des envois ({logs.length})</button>
      </div>

      {tab === "scenarios" && (
        <>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="automation-category-filters">
            <button onClick={() => setCatFilter("all")} data-testid="cat-filter-all"
              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${catFilter === "all" ? "bg-bordeaux text-white border-bordeaux" : "bg-canvas text-marine border-marine/20 hover:border-marine/50"}`}>
              Toutes ({items.length})
            </button>
            {categories.map(([cat, count]) => (
              <button key={cat} onClick={() => setCatFilter(cat)} data-testid={`cat-filter-${cat}`}
                className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${catFilter === cat ? "bg-bordeaux text-white border-bordeaux" : "bg-canvas text-marine border-marine/20 hover:border-marine/50"}`}>
                {cat} ({count})
              </button>
            ))}
          </div>
          <div className="bg-sable rounded-sm border border-marine/10 overflow-hidden" data-testid="automations-list">
            <div className="hidden md:grid grid-cols-[2fr_1.6fr_1.4fr_1.1fr_70px_32px] gap-4 px-5 py-3 text-xs uppercase tracking-wider text-marine/80 border-b border-marine/10 bg-sable">
              <span>Automatisation</span><span>Déclencheur</span><span>Destinataires</span><span>Délai</span><span>Statut</span><span></span>
            </div>
            {filteredItems.length === 0 && <p className="text-sm text-marine/80 p-5">Aucune automatisation dans cette catégorie.</p>}
            {filteredItems.map((a) => (
              <div key={a.id} data-testid={`automation-${a.id}`} className="grid md:grid-cols-[2fr_1.6fr_1.4fr_1.1fr_70px_32px] gap-4 px-5 py-4 border-b border-marine/5 items-center bg-creme hover:bg-sable">
                <button onClick={() => openEdit(a)} data-testid={`automation-edit-${a.id}`} className="text-left"><div className="font-medium text-marine flex items-center gap-1.5"><Icons.Mail size={16} className="text-bordeaux" /> {a.name}</div><div className="text-xs text-marine/80 truncate">{a.subject}</div></button>
                <span className="text-sm text-marine">{a.event_label}</span>
                <span className="text-xs text-marine/80">{(a.recipients || []).map((r) => roleLabel[r] || r).join(", ")}</span>
                <span className="text-sm text-marine/80">{delayText(a)}</span>
                <Switch checked={!!a.active} onChange={() => toggleActive(a)} testid={`automation-toggle-${a.id}`}
                  title={a.active ? "Désactiver cette automatisation" : "Activer cette automatisation"} className="justify-self-start md:justify-self-center" />
                <button onClick={() => del(a)} data-testid={`automation-delete-${a.id}`} className="justify-self-end text-rouge/70 hover:text-rouge transition-colors" title="Supprimer"><Icons.Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "history" && (
        <div className="bg-sable rounded-sm border border-marine/10 overflow-hidden" data-testid="email-logs">
          <div className="hidden md:grid grid-cols-[2fr_2fr_1.5fr_1fr] gap-4 px-5 py-3 text-xs uppercase tracking-wider text-marine/80 border-b border-marine/10 bg-sable">
            <span>Destinataire</span><span>Objet / Contexte</span><span>Date</span><span>Statut</span>
          </div>
          {logs.length === 0 && <p className="text-sm text-marine/80 p-5">Aucun e-mail envoyé pour le moment.</p>}
          {logs.map((l) => (
            <div key={l.id} data-testid={`log-${l.id}`} className="grid md:grid-cols-[2fr_2fr_1.5fr_1fr] gap-4 px-5 py-3 border-b border-marine/5 items-center text-sm bg-creme">
              <span className="text-marine truncate">{l.to_email}</span>
              <span className="text-marine/80 truncate">{l.subject}{l.context_label ? ` · ${l.context_label}` : ""}</span>
              <span className="text-marine/80">{fmtDateTime(l.created_at)}</span>
              <span><span className={`text-[9px] uppercase tracking-wider px-2 py-1 rounded-sm ${STATUS_STYLE[l.status] || "bg-sable-clair text-marine/80"}`}>{l.status}</span>{l.error ? <span className="block text-xs text-rouge mt-0.5 truncate" title={l.error}>{l.error}</span> : null}</span>
            </div>
          ))}
        </div>
      )}

      {editor && (
        <Modal onClose={() => setEditor(null)} title={editor.mode === "create" ? "Nouvelle automatisation" : `Modifier — ${form.name}`} wide>
          <div className="grid lg:grid-cols-2 gap-6" data-testid="automation-editor">
            <div className="space-y-4">
              <label className="text-sm block">Nom de l'automatisation<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" className="inp mt-1" placeholder="Ex : Bienvenue nouvelle entreprise" /></label>

              <label className="text-sm block">Déclencheur (événement)
                <select value={form.event} onChange={(e) => onEventChange(e.target.value)} data-testid="edit-event" className="inp mt-1">
                  {catalog.events.map((e) => <option key={e.key} value={e.key}>{e.category} — {e.label}</option>)}
                </select>
              </label>

              <div>
                <div className="text-sm mb-1.5">Destinataires</div>
                <div className="flex flex-wrap gap-2" data-testid="edit-recipients">
                  {(currentEvent?.recipients || []).map((r) => (
                    <button key={r} type="button" onClick={() => toggleRecipient(r)} data-testid={`recipient-${r}`}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${form.recipients.includes(r) ? "bg-marine text-white border-marine" : "bg-white text-marine border-marine/25"}`}>
                      {form.recipients.includes(r) && <Icons.Check size={16} className="inline mr-1" />}{roleLabel[r] || r}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-sm mb-1.5">Délai d'envoi</div>
                {currentEvent?.mode === "scheduled" ? (
                  <div className="flex items-center gap-2 text-sm" data-testid="edit-delay-scheduled">
                    <input type="number" min="0" value={form.delay_value} onChange={(e) => setForm({ ...form, delay_value: parseInt(e.target.value) || 0 })} data-testid="edit-delay-value" className="inp w-20" />
                    <select value={form.delay_unit} onChange={(e) => setForm({ ...form, delay_unit: e.target.value })} data-testid="edit-delay-unit" className="inp w-32">
                      {(catalog.delay_units || []).map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
                    </select>
                    <span className="text-marine/80">{currentEvent.schedule_direction === "before" ? "avant l'échéance" : "après l'échéance"}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm" data-testid="edit-delay-event">
                    <input type="number" min="0" value={form.delay_value} onChange={(e) => setForm({ ...form, delay_value: parseInt(e.target.value) || 0 })} data-testid="edit-delay-value" className="inp w-20" />
                    <select value={form.delay_unit} onChange={(e) => setForm({ ...form, delay_unit: e.target.value })} data-testid="edit-delay-unit" className="inp w-32">
                      {(catalog.delay_units || []).map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
                    </select>
                    <span className="text-marine/80">après l'événement (0 = immédiat)</span>
                  </div>
                )}
              </div>

              <label className="text-sm block">Objet<input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="edit-subject" className="inp mt-1" /></label>
              <label className="text-sm block">Contenu<textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="edit-body" rows={6} className="inp mt-1 w-full resize-y" /></label>
              <label className="text-sm block">Signature<input value={form.signature} onChange={(e) => setForm({ ...form, signature: e.target.value })} data-testid="edit-signature" className="inp mt-1" /></label>
              <div>
                <div className="text-xs uppercase tracking-wider text-bordeaux font-semibold mb-1.5">Variables disponibles</div>
                <div className="flex flex-wrap gap-1.5">
                  {(currentEvent?.variables || []).map((v) => <code key={v} className="text-[9px] px-2 py-1 rounded-sm bg-sable border border-marine/30 text-marine">{`{${v}}`}</code>)}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="text-xs uppercase tracking-wider text-bordeaux font-semibold">Prévisualisation (données d'exemple)</div>
              <div className="rounded-sm border border-marine/10 overflow-hidden" data-testid="email-preview">
                <div className="bg-marine px-4 py-3 text-white text-sm font-medium">{previewSubject}</div>
                <div className="p-4 text-sm text-marine" dangerouslySetInnerHTML={richHtml(previewBody)} />
              </div>
              <div className="border-t border-marine/10 pt-4">
                <div className="text-xs uppercase tracking-wider text-bordeaux font-semibold mb-2">Envoyer un e-mail de test</div>
                {editor.mode === "create"
                  ? <p className="text-xs text-marine/80">Enregistrez d'abord l'automatisation, puis rouvrez-la pour envoyer un test.</p>
                  : (
                    <div className="flex gap-2">
                      <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="test-email" placeholder="adresse@exemple.com" className="inp flex-1" />
                      <button onClick={sendTest} data-testid="send-test" className="border border-marine text-marine rounded-sm px-4 text-sm hover:bg-marine hover:text-white transition-colors whitespace-nowrap">Envoyer le test</button>
                    </div>
                  )}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-marine/10">
            <button onClick={() => setEditor(null)} className="px-4 py-2 text-sm text-marine/80">Annuler</button>
            <button onClick={save} data-testid="save-template" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover">{editor.mode === "create" ? "Créer l'automatisation" : "Enregistrer"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
